/* eslint-disable no-undef */
import { Client } from '../../../types/api';
import { StadiumFanAnalytics, AnalyticsInput } from '../../../types/inVenue';
import useAnalytics from './useAnalytics';
import colors from '../../../config/color-palette';
import { shortDate } from '../../../utils/formatters';

export const MAX_CROWD_FLOW_GAMES = 10;

const useCrowdFlow = ({
    rightsholder,
    season,
    client,
    gameDates,
    sectionIds,
}: Omit<AnalyticsInput, 'groupBy' | 'clientId'> & { client: Client }) => {
    const { data: analytics, isLoading } = useAnalytics({
        clientId: client.id,
        rightsholder,
        season,
        gameDates,
        sectionIds,
        groupBy: ['moment', 'date'],
    });

    const momentPerDate = (analytics || []).reduce(
        (acc: any, curr: StadiumFanAnalytics) => {
            if (!acc[curr.gameDate]) {
                acc[curr.gameDate] = {
                    [curr.momentStart]: {
                        fanCount: curr.fanCount,
                        timeFromGameStart: curr.timeFromGameStart,
                    },
                };
            } else {
                acc[curr.gameDate] = {
                    ...acc[curr.gameDate],
                    [curr.momentStart]: {
                        fanCount: curr.fanCount,
                        timeFromGameStart: curr.timeFromGameStart,
                    },
                };
            }
            return acc;
        },
        {},
    );

    const colorList =
        Object.keys(momentPerDate).length <= 2
            ? [client.primaryColor, client.secondaryColor]
            : colors;

    const data = Object.entries(momentPerDate)
        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
        .slice(0, MAX_CROWD_FLOW_GAMES)
        .reduce(
            (acc: any, [date, moments]) => {
                const minutesBeforeGame = Object.values(moments).reduce(
                    (acc: any, curr: any) => {
                        if (curr.timeFromGameStart < 0) {
                            acc += 15;
                        }
                        return acc;
                    },
                    0,
                );
                Object.entries(moments)
                    .sort(
                        ([a], [b]) =>
                            new Date(a).getTime() - new Date(b).getTime(),
                    )
                    .forEach(([, data], index) => {
                        const label = `${-minutesBeforeGame + index * 15}`;
                        if (!acc.labels.includes(label)) {
                            acc.labels.push(label);
                        }
                        const formattedDate = shortDate(date);
                        const dataset = acc.datasets.find(
                            (dataset: any) => dataset.label === formattedDate,
                        );
                        if (!dataset) {
                            acc.datasets.push({
                                label: formattedDate,
                                data: [data.fanCount],
                                color: colorList[
                                    acc.datasets.length % colors.length
                                ],
                            });
                        } else {
                            dataset.data.push(data.fanCount);
                        }
                    });

                return acc;
            },
            {
                labels: [],
                datasets: [],
            },
        );

    return { data, isLoading };
};

export default useCrowdFlow;
